import { getVariable } from "#shared/config/config.ts";

// Catalog docsUrl values are paths (`/integrations/aws`). DOCS_ORIGIN in
// config.json is https://docs.gain.security in prod and the app origin
// plus /docs on every other stage. A missing origin means this page's /docs.

export const DOCS_CANONICAL_ORIGIN = "https://docs.gain.security";
export const DOCS_CANONICAL_HOST = "docs.gain.security";
export const APP_CANONICAL_ORIGIN = "https://app.gain.security";

export function buildDocsUrl(path: string, origin: string): string {
  const docsPath = docsPathname(path);
  if (docsPath == null) return path;
  return `${origin.replace(/\/$/, "")}${docsPath}`;
}

function docsPathname(href: string): string | null {
  const path =
    href.startsWith("/") && !href.startsWith("//") ? href : docsRest(href);
  if (path == null) return null;
  return withoutDocsMount(path);
}

function docsRest(href: string): string | null {
  if (!href.startsWith(DOCS_CANONICAL_ORIGIN)) return null;
  const rest = href.slice(DOCS_CANONICAL_ORIGIN.length);
  if (
    rest !== "" &&
    !rest.startsWith("/") &&
    !rest.startsWith("?") &&
    !rest.startsWith("#")
  ) {
    return null;
  }
  return rest || "/";
}

// Callers may write `/integrations/aws` or the explicit `/docs/integrations/aws`.
// The origin already carries the mount (`/docs` on an app host, the site root
// on docs.gain.security), so the mount is not applied a second time.
function withoutDocsMount(path: string): string {
  if (path === "/docs") return "/";
  if (
    !path.startsWith("/docs/") &&
    !path.startsWith("/docs?") &&
    !path.startsWith("/docs#")
  ) {
    return path;
  }
  return path.slice("/docs".length) || "/";
}

function docsOrigin(): string {
  try {
    const configured = getVariable("DOCS_ORIGIN");
    if (configured) return configured;
  } catch {
    // Tests and Storybook render before config.json is loaded.
  }
  if (typeof window !== "undefined") return `${window.location.origin}/docs`;
  return DOCS_CANONICAL_ORIGIN;
}

export function docsHref(href: string): string {
  return buildDocsUrl(href, docsOrigin());
}

// Prod docs live on their own host. Every other stage serves docs from the app host.
export function appHref(hostname: string, origin: string): string {
  if (hostname === DOCS_CANONICAL_HOST) return APP_CANONICAL_ORIGIN;
  return origin;
}

export function docsBasePath(
  configured: string | undefined,
  hostname: string,
): string {
  if (hostname === DOCS_CANONICAL_HOST) return "/";
  return configured ?? "/docs";
}

export function docsReturnUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.origin !== DOCS_CANONICAL_ORIGIN) return null;
  if (url.username || url.password) return null;
  return `${url.origin}${url.pathname}${url.search}${url.hash}`;
}
